import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../../core/api';

export default function ProductPicker({ value, onSelect, onManualChange }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ left: 0, width: 0 });

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const searchEnabledRef = useRef(false);

  // همگام‌سازی با مقدار بیرونی
  useEffect(() => {
    if ((value || '') !== query) {
      searchEnabledRef.current = false;
      setQuery(value || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // جستجو با debounce
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!searchEnabledRef.current) return;

    if (query.trim().length < 1) {
      setResults([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/products/search', {
          params: { q: query.trim() },
        });
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.products || [];
        setResults(list);
        setOpen(true);
      } catch (err) {
        console.error('خطا در جستجوی محصول:', err);
        setResults([]);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // محاسبهٔ موقعیت لیست زیر input (fixed نسبت به viewport)
  const updatePosition = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxH = 250;
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const spaceAbove = r.top - 8;

    // فقط وقتی پایین جای کم دارد و بالا بیشتر جا دارد، به بالا باز شود
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;

    if (openUp) {
      setPos({
        bottom: window.innerHeight - r.top + 4, // چسبیده به بالای input
        left: r.left,
        width: Math.max(r.width, 280),
        maxHeight: Math.min(maxH, spaceAbove),
      });
    } else {
      setPos({
        top: r.bottom + 4,
        left: r.left,
        width: Math.max(r.width, 280),
        maxHeight: Math.min(maxH, spaceBelow),
      });
    }
  };
  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();

    // با اسکرول صفحه/جدول یا resize، موقعیت به‌روز شود
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, results.length, loading]);

  // بستن با کلیک بیرون (input و dropdown هر دو داخل حساب می‌شوند)
  useEffect(() => {
    const handler = (e) => {
      if (
        inputRef.current?.contains(e.target) ||
        dropdownRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInput = (e) => {
    searchEnabledRef.current = true;
    setQuery(e.target.value);
    onManualChange?.(e.target.value);
  };

  const handlePick = (product) => {
    searchEnabledRef.current = false;
    setQuery(product.name || '');
    setOpen(false);
    onSelect(product);
  };

  const dropdown = open
    ? createPortal(
        <div
          ref={dropdownRef}
          className="product-dropdown"
          style={{
            position: 'fixed',
            top: pos.top,
            bottom: pos.bottom,
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxHeight,
          }}
        >
          {loading ? (
            <div className="customer-search-empty">در حال جستجو...</div>
          ) : results.length === 0 ? (
            <div className="customer-search-empty">محصولی یافت نشد</div>
          ) : (
            results.map((p) => (
              <div
                key={p.id}
                className="customer-result"
                onClick={() => handlePick(p)}
              >
                <div className="customer-result-name">{p.name}</div>
                <div className="customer-result-info">
                  {p.code ? `کد: ${p.code}` : ''}
                  {p.unit ? ` - واحد: ${p.unit}` : ''}
                  {p.price
                    ? ` - ${Number(p.price).toLocaleString('fa-IR')} ریال`
                    : ''}
                </div>
              </div>
            ))
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInput}
        onFocus={() => {
          if (results.length > 0) setOpen(true);
        }}
        placeholder="جستجوی نام یا کد کالا..."
        autoComplete="off"
      />
      {dropdown}
    </>
  );
}
